import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Combobox, Group, Pill, PillsInput, Stack, useCombobox } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFirstAidKit } from '@tabler/icons-react';
import { t } from 'i18next';
import { IFacilitySelection } from '../../../entities/healthcareProfessional/HealthcareProfessional.ts';
import AffiliatedFacilityModal from '../../../../pages/HcpDashboard/Modal/AffiliatedFacilityModal.tsx';
import { IHealthcareFacility } from '../../../entities/healthcareFacility/HealthcareFacility.ts';

export type IFacilityMultiSelectProps = {
    value?: IFacilitySelection[];
    onChange: (values: IFacilitySelection[]) => void;
    isError: boolean;
    hcfData?: IHealthcareFacility[];
};

const FacilityMultiSelect: React.FC<IFacilityMultiSelectProps> = inject()(
    observer(({ value = [], onChange, isError, hcfData = [] }) => {
        const [search, setSearch] = useState('');
        const [opened, { open, close }] = useDisclosure(false);
        const [currentFacility, setCurrentFacility] = useState<IFacilitySelection | null>(null);

        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
            onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
        });

        const handleAddFacility = (facility: IFacilitySelection) => {
            if (!facility) return;

            const exists = value.some((f) => f.healthcareFacilityID === facility.healthcareFacilityID);

            if (exists) 
                onChange(value.map((f) => (f.healthcareFacilityID === facility.healthcareFacilityID ? facility : f)));
             else 
                onChange([...value, facility]);
            
        };

        const handleRemoveFacility = (facilityId: string | undefined) => {
            onChange(value.filter((f) => f.healthcareFacilityID !== facilityId));
        };

        const openModalAssistant = (facility: IFacilitySelection) => {
            setCurrentFacility(facility);
            open();
        };

        const pills = useMemo(() => {
            return value.map((item) => {
                const facilityName = hcfData?.find((hcf) => hcf.id.toString() === item.healthcareFacilityID)?.name;

                return (
                    <Pill
                        key={`${item.healthcareFacilityID}-${item.assistantEmail ?? ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            openModalAssistant({
                                facilityName: facilityName ?? '',
                                healthcareFacilityID: item.healthcareFacilityID,
                                assistant: item.assistant,
                                assistantEmail: item.assistantEmail,
                            });
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        {`${facilityName}${item.assistant ? ` - ${item.assistant}` : ''}`}
                    </Pill>
                );
            });
        }, [value, hcfData]);

        const options = hcfData?.map((item) => (
            <Combobox.Option
                value={item.name}
                key={item.id}
                onClick={(e) => {
                    e.stopPropagation();
                    combobox.closeDropdown();
                    openModalAssistant({
                        facilityName: item.name,
                        healthcareFacilityID: item.id!.toString(),
                    });
                }}
            >
                <Group gap="sm">
                    <span>{item.name}</span>
                </Group>
            </Combobox.Option>
        ));

        return (
            <Stack gap="xxs" w="100%">
                <Combobox store={combobox} withinPortal={false}>
                    <Combobox.DropdownTarget>
                        <PillsInput
                            leftSection={<IconFirstAidKit size={16} />}
                            styles={{ input: { borderColor: isError ? 'red' : 'var(--input-bd)' } }}
                            onClick={() => combobox.openDropdown()}
                        >
                            <Pill.Group>
                                {pills}
                                <Combobox.EventsTarget>
                                    <PillsInput.Field
                                        onClick={() => combobox.toggleDropdown()}
                                        value={search}
                                        placeholder={t('formHcf.selectHealthFacilityType')}
                                        onChange={(event) => setSearch(event.currentTarget.value)}
                                    />
                                </Combobox.EventsTarget>
                            </Pill.Group>
                        </PillsInput>
                    </Combobox.DropdownTarget>
                    <Combobox.Dropdown>
                        <Combobox.Options>{options}</Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
                <AffiliatedFacilityModal
                    opened={opened}
                    close={close}
                    facility={currentFacility || undefined}
                    handleRemoveFacility={handleRemoveFacility}
                    handleAddFacility={handleAddFacility}
                />
            </Stack>
        );
    }),
);

export default FacilityMultiSelect;
